/**
 * Smoothly scrolls to a destination element or Y position.
 *
 * @param {string|number|HTMLElement} destination - Anchor selector (e.g., '#section'), Y position (number), or DOM element.
 * @param {Object} [options] - Smooth scroll options.
 * @param {number} [options.duration=500] - Scroll duration in milliseconds.
 * @param {string} [options.easing='ease'] - Easing function name.
 */

export const smoothScroll = (destination, options = {}) => {
  const { duration = 500, easing = 'ease-in-out' } = options;

  const easings = {
    linear: (t) => t,
    ease: (t) => 0.5 * (1 - Math.cos(Math.PI * t)),
    'ease-in': (t) => t * t,
    'ease-out': (t) => t * (2 - t),
    'ease-in-out': (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
    'ease-in-cubic': (t) => t ** 3,
    'ease-out-cubic': (t) => (--t) ** 3 + 1,
    'ease-in-quart': (t) => t ** 4,
    'ease-out-quart': (t) => 1 - (--t) ** 4,
    'ease-in-quint': (t) => t ** 5,
    'ease-out-quint': (t) => 1 + (--t) ** 5,
    'ease-in-sine': (t) => 1 - Math.cos((t * Math.PI) / 2),
    'ease-out-sine': (t) => Math.sin((t * Math.PI) / 2),
    'ease-in-expo': (t) => (t === 0 ? 0 : 2 ** (10 * (t - 1))),
    'ease-out-expo': (t) => (t === 1 ? 1 : 1 - 2 ** (-10 * t)),
    'ease-in-circ': (t) => 1 - Math.sqrt(1 - t * t),
    'ease-out-circ': (t) => Math.sqrt(1 - --t * t),
  };

  const start = window.pageYOffset;
  let targetY;

  if (typeof destination === 'number') {
    targetY = destination;
  } else if (typeof destination === 'string') {
    const el = document.querySelector(destination);
    if (!el) return;
    targetY = el.getBoundingClientRect().top + window.pageYOffset;
  } else if (destination instanceof HTMLElement) {
    targetY = destination.getBoundingClientRect().top + window.pageYOffset;
  } else {
    throw new Error('Invalid destination type');
  }

  const distance = targetY - start;
  const easingFn = easings[easing] || easings['ease-in-out'];

  let startTime = null;

  const scrollStep = (timestamp) => {
    if (!startTime) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easeProgress = easingFn(progress);

    window.scrollTo(0, start + distance * easeProgress);

    if (elapsed < duration) {
      requestAnimationFrame(scrollStep);
    } else {
      window.scrollTo(0, targetY);
    }
  };

  requestAnimationFrame(scrollStep);
};
